import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCurrentUserContext } from '@/contexts';
import { ProfileFragment } from '@/fragments';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import type {
  SetInitialNameMutation,
  SetInitialNameMutationVariables,
} from './useSetInitialName.generated';

const query = gql`
  ${ProfileFragment}
  mutation SetInitialName($name: String!) {
    updateProfile(name: $name) {
      ...Profile
    }
  }
`;

/**
 * Names a freshly created account from the auth panel.
 *
 * Deliberately not `useUpdateProfile`: that hook is built around the settings
 * form's avatar upload and refetches entries and experiences on success, none of
 * which applies a second after signup.
 */
export const useSetInitialName = () => {
  const { setUser } = useCurrentUserContext();

  const { mutate, ...result } = useMutation<
    SetInitialNameMutation,
    QueryError,
    SetInitialNameMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        SetInitialNameMutation,
        SetInitialNameMutationVariables
      >(query, variables),
  });

  const setName = (
    name: string,
    options?: MutateOptions<
      SetInitialNameMutation,
      QueryError,
      SetInitialNameMutationVariables
    >
  ) => {
    mutate(
      { name },
      {
        ...options,
        onSuccess: (data, ...args) => {
          setUser(data.updateProfile);
          options?.onSuccess?.(data, ...args);
        },
      }
    );
  };

  return {
    ...result,
    setName,
  };
};
